import { useEffect, useRef, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import {
	type Instruction,
	extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { expVal } from '@atlassian/jira-feature-experiments';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isDraggableProject } from '../common/draggable-project.tsx';
import { AFTER, BEFORE, type MenuItemReorderOperation } from '../utils/constants.tsx';
import { useUpdateFavouriteOrder } from '../services/update-favourite-order/useUpdateFavouriteOrder.tsx';
import { ANALYTIC_PAYLOAD } from '../common/constants.tsx';

const useDropTargetForProjectsConditionally = () => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [state, setState] = useState<'idle' | 'is-over'>();
	const updateFavouriteOrder = useUpdateFavouriteOrder();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsEvent = createAnalyticsEvent({});

	useEffect(() => {
		const element = ref.current;

		// might have no projects
		if (!element) {
			return;
		}

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => {
				return isDraggableProject(source.data);
			},
			onDragStart({ source }) {
				setState('is-over');

				const dragging = source.data;
				fireUIAnalytics(analyticsEvent, {
					...ANALYTIC_PAYLOAD.DRAG_STARTED,
					attributes: {
						...ANALYTIC_PAYLOAD.ATTRIBUTES,
						itemId: dragging.id,
					},
				});
			},
			onDragEnter({ source }) {
				setState('is-over');

				const dragging = source.data;
				fireUIAnalytics(analyticsEvent, {
					...ANALYTIC_PAYLOAD.DRAG_ENTERED,
					attributes: {
						...ANALYTIC_PAYLOAD.ATTRIBUTES,
						itemId: dragging.id,
					},
				});
			},
			onDragLeave({ source }) {
				setState('idle');

				const dragging = source.data;
				fireUIAnalytics(analyticsEvent, {
					...ANALYTIC_PAYLOAD.DRAG_LEFT,
					attributes: {
						...ANALYTIC_PAYLOAD.ATTRIBUTES,
						itemId: dragging.id,
					},
				});
			},
			onDrop({ source, location }) {
				setState('idle');

				let successfulReorder = false;
				const dragging = source.data;
				const [innerMost] = location.current.dropTargets;
				const dropTargetData = innerMost.data;

				if (!isDraggableProject(dragging) || !isDraggableProject(dropTargetData)) {
					fireUIAnalytics(analyticsEvent, {
						...ANALYTIC_PAYLOAD.DRAG_ENDED,
						attributes: {
							...ANALYTIC_PAYLOAD.ATTRIBUTES,
							itemId: dragging.id,
							successfulReorder,
						},
					});
					return;
				}

				const instruction = extractInstruction(dropTargetData);
				const operation = instruction && instructionToOperation(instruction);

				if (operation) {
					updateFavouriteOrder(operation, dragging.id, dropTargetData.id);
					successfulReorder = true;

					if (!source.element) {
						return;
					}
					triggerPostMoveFlash(source.element);
				}

				fireUIAnalytics(analyticsEvent, {
					...ANALYTIC_PAYLOAD.DRAG_ENDED,
					attributes: {
						...ANALYTIC_PAYLOAD.ATTRIBUTES,
						itemId: dragging.id,
						successfulReorder,
					},
				});
			},
		});
	});

	return { ref, state };
};

export const useDropTargetForProjects = functionWithCondition(
	() => expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
	useDropTargetForProjectsConditionally,
	() => ({ ref: { current: null }, state: undefined }),
);

const instructionToOperation = (instruction: Instruction): MenuItemReorderOperation | null => {
	switch (instruction?.operation) {
		case 'reorder-before':
			return BEFORE;
		case 'reorder-after':
			return AFTER;
		default:
			return null;
	}
};
